import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, X } from "lucide-react";
import ProductPicker from "./ProductPicker";

/** Inline (no modal) kit editor — create, edit, delete. Admins + operations only. */
export default function KitEditor({ kit, onDone }) {
  const [name, setName] = useState(kit?.name || "");
  const [items, setItems] = useState(kit?.items?.map((i) => ({ ...i })) || []);
  const [busy, setBusy] = useState(false);
  const addItem = (p) => setItems((xs) => xs.some((x) => x.product_id === p.id) ? xs.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x)
    : [...xs, { product_id: p.id, product_name: p.product_name, part_number: p.oem_part_number || p.sku, qty: 1 }]);
  const save = async () => {
    if (name.trim().length < 2) return toast.error("Give the kit a name");
    if (!items.length) return toast.error("Add at least one product");
    setBusy(true);
    const body = { name: name.trim(), items: items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty) || 1 })) };
    try { await (kit ? api.put(`/dc-kits/${kit.id}`, body) : api.post("/dc-kits", body)); toast.success(kit ? "Kit updated" : "Kit saved"); onDone(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save kit"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete kit "${kit.name}"? DCs that already used it are not changed.`)) return;
    await api.delete(`/dc-kits/${kit.id}`).then(() => { toast.success("Kit deleted"); onDone(true); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };
  return (
    <div className="rounded border border-slate-200 bg-white p-2.5 space-y-2" data-testid="dc-kit-editor">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='Kit name, e.g. "Laptop + Charger + Bag"' className="h-8 text-[12px]" data-testid="dc-kit-name" />
      <ul className="space-y-1" data-testid="dc-kit-editor-items">
        {items.map((it) => (
          <li key={it.product_id} className="flex items-center gap-2 text-[12px]" data-testid={`dc-kit-item-${it.product_id}`}>
            <span className="min-w-0 flex-1 truncate">{it.product_name}</span>
            <Input type="number" min="1" max="50" value={it.qty} onChange={(e) => setItems((xs) => xs.map((x) => x.product_id === it.product_id ? { ...x, qty: e.target.value } : x))}
              className="h-7 w-16 text-[12px]" aria-label="Quantity" data-testid={`dc-kit-item-qty-${it.product_id}`} />
            <button type="button" onClick={() => setItems((xs) => xs.filter((x) => x.product_id !== it.product_id))} aria-label="Remove item" data-testid={`dc-kit-item-remove-${it.product_id}`}><X className="h-3.5 w-3.5 text-slate-400 hover:text-slate-800" /></button>
          </li>
        ))}
      </ul>
      <ProductPicker onPick={addItem} testId="dc-kit-pick" placeholder="Add a product to this kit…" autoFocus={!kit} />
      <div className="flex items-center gap-1.5">
        {kit && <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-700 hover:bg-rose-50" onClick={remove} data-testid="dc-kit-delete"><Trash2 className="h-3 w-3 mr-1" />Delete kit</Button>}
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-[11px]" onClick={() => onDone(false)} data-testid="dc-kit-editor-cancel">Cancel</Button>
        <Button size="sm" className="h-7 text-[11px]" disabled={busy} onClick={save} data-testid="dc-kit-save">{busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Save kit</Button>
      </div>
    </div>
  );
}
